import { CONNECTOR_END_STYLE, STROKE_TYPES } from '../constants';
import { type StylusConnectorOptions } from '../stylus/StylusConnector';
import { type IconWithOffset, type OmitFirst } from '../types';
import { capitalize, wrapText } from '../utils/string';
import arrowRight from './icons/arrowRight.svg';
import arrowRightSolid from './icons/arrowRightSolid.svg';
import arrowUp from './icons/arrowUp.svg';
import arrowUpBordered from './icons/arrowUpBordered.svg';
import arrowUpBorderedDashed from './icons/arrowUpBorderedDashed.svg';
import arrowUpSolid from './icons/arrowUpSolid.svg';
import bolt from './icons/bolt.svg';
import boltBordered from './icons/boltBordered.svg';
import boltSolid from './icons/boltSolid.svg';
import circle from './icons/circle.svg';
import circleSolid from './icons/circleSolid.svg';
import clock from './icons/clock.svg';
import clockBordered from './icons/clockBordered.svg';
import clockBorderedDashed from './icons/clockBorderedDashed.svg';
import cross from './icons/cross.svg';
import crossSolid from './icons/crossSolid.svg';
import gear from './icons/gear.svg';
import hand from './icons/hand.svg';
import lines from './icons/lines.svg';
import message from './icons/message.svg';
import messageBordered from './icons/messageBordered.svg';
import messageBorderedDashed from './icons/messageBorderedDashed.svg';
import messageSolid from './icons/messageSolid.svg';
import note from './icons/note.svg';
import noteBordered from './icons/noteBordered.svg';
import noteBorderedDashed from './icons/noteBorderedDashed.svg';
import pentagon from './icons/pentagon.svg';
import pentagonBordered from './icons/pentagonBordered.svg';
import pentagonBorderedDashed from './icons/pentagonBorderedDashed.svg';
import pentagonBorderedDouble from './icons/pentagonBorderedDouble.svg';
import pentagonSolid from './icons/pentagonSolid.svg';
import plusBordered from './icons/plusBordered.svg';
import plusSolid from './icons/plusSolid.svg';
import plusSolidBordered from './icons/plusSolidBordered.svg';
import plusSolidBorderedDashed from './icons/plusSolidBorderedDashed.svg';
import rewind from './icons/rewind.svg';
import rewindBordered from './icons/rewindBordered.svg';
import rewindSolid from './icons/rewindSolid.svg';
import script from './icons/script.svg';
import star from './icons/star.svg';
import table from './icons/table.svg';
import triangle from './icons/triangle.svg';
import triangleBordered from './icons/triangleBordered.svg';
import triangleBorderedDashed from './icons/triangleBorderedDashed.svg';
import triangleSolid from './icons/triangleSolid.svg';
import user from './icons/user.svg';
import lane from './swimlaneIcons/lane.svg';
import pool from './swimlaneIcons/pool.svg';

export type BusinessProcessShapeNames =
  | 'businessProcessEvent'
  | 'businessProcessActivity'
  | 'businessProcessGateway'
  | 'businessProcessData'
  | 'businessProcessDataStore';

export type BusinessProcessBorderStyle =
  | 'regular'
  | 'dashed'
  | 'thick'
  | 'double'
  | 'double-dashed';

export interface BusinessProcessProperties {
  borderStyle?: BusinessProcessBorderStyle;
  iconName?: IconWithOffset;
  secondaryIconName?: IconWithOffset;
}

// defines rendering options for a business process shape
export interface BusinessProcessShape {
  variant: string; // needed?
  name: string;
  featured?: boolean;

  // properties read from the definition directly, not needed on the shape object
  properties?: BusinessProcessProperties;
}
type BusinessProcessShapes = Record<
  BusinessProcessShapeNames,
  { title: string; shapes: BusinessProcessShape[] }
>;

const makeEvent = (
  when: 'start' | 'intermediate' | 'end',
  event: string | undefined,
  iconName?: IconWithOffset,
  interrupting?: boolean // if undefined, no prefix
): BusinessProcessShape => {
  const prefix =
    interrupting !== undefined
      ? interrupting
        ? 'Interrupting - '
        : 'Non-interrupting - '
      : '';

  const name = capitalize(
    `${prefix}${event ? `${event} ` : ''}${when} event`.trim()
  );
  const variant = `${interrupting ? 'interrupting' : 'non-interrupting'}-${(
    event ?? 'none'
  ).replace(/\s+/g, '-')}-${when}`;

  const borderStyle =
    when === 'end'
      ? 'thick'
      : when === 'intermediate'
      ? !interrupting
        ? 'double-dashed'
        : 'double'
      : !interrupting
      ? 'dashed'
      : undefined;

  return {
    variant,
    name,
    properties: { iconName, borderStyle },
  };
};

type EventArgs = OmitFirst<Parameters<typeof makeEvent>>;
const startEvent = (...args: EventArgs) => makeEvent('start', ...args);
const intEvent = (...args: EventArgs) => makeEvent('intermediate', ...args);
const endEvent = (...args: EventArgs) => makeEvent('end', ...args);

const task = (name: string, iconName: IconWithOffset) => ({
  variant: `${name.replace(/ /g, '-').toLowerCase()}-task`,
  name: `${name} task`,
  properties: { iconName },
});

const subProcess = (
  name: string,
  iconName: IconWithOffset,
  interrupting: boolean
): BusinessProcessShape => ({
  variant: `${interrupting ? 'interrupting' : 'non-interrupting'}-${name
    .replace(/ /g, '-')
    .toLowerCase()}-sub-process`,
  name: `${
    interrupting ? 'Interrupting' : 'Non-interrupting'
  } - ${name} event sub-process - collapsed`,
  properties: { iconName, borderStyle: 'dashed' },
});

const callActivity = (
  name?: string,
  iconName?: IconWithOffset
): BusinessProcessShape => ({
  variant: name ? `${name.toLowerCase()}-call-activity` : 'call-activity',
  name: name ? `${name} call activity` : 'Call activity',
  properties: { borderStyle: 'thick', iconName },
});

const gateway = (
  name: string,
  iconName?: IconWithOffset
): BusinessProcessShape => ({
  variant: name.replace(/[\s-]+/g, '-').toLowerCase(),
  name,
  properties: { iconName },
});

const dataObj = (
  name: string,
  iconName?: IconWithOffset,
  secondaryIconName?: IconWithOffset
): BusinessProcessShape => ({
  variant: name.replace(/[\s-]+/g, '-').toLowerCase(),
  name,
  properties: { iconName, secondaryIconName },
});

const svg = (svg: string) => ({ type: 'svg' as const, svg });

export const businessProcessShapes: BusinessProcessShapes = {
  businessProcessEvent: {
    title: 'Events',
    shapes: [
      { ...startEvent(undefined, undefined, true), featured: true },
      startEvent('timer', svg(clock), true),
      startEvent('timer', svg(clock), false),
      startEvent('conditional', svg(note), true),
      startEvent('conditional', svg(note), false),
      startEvent('signal', svg(triangle), true),
      startEvent('signal', svg(triangle), false),
      startEvent('multiple', svg(pentagon), true),
      startEvent('multiple', svg(pentagon), false),
      startEvent('parallel multiple', svg(cross), true),
      startEvent('escalation', svg(cross), true),
      startEvent('escalation', svg(arrowUp), false),
      startEvent('error', svg(arrowUp), true),
      startEvent('message', svg(message), true),
      startEvent('message', svg(message), false),
      startEvent('compensation', svg(rewind), true),

      { ...intEvent(undefined, undefined, true), featured: true },

      intEvent('catch - message', svg(message)),
      intEvent('boundary - catch - message', svg(message), true),
      intEvent('boundary - catch - message', svg(message), false),
      intEvent('throw - message', svg(messageSolid)),

      intEvent('timer', svg(clock)),
      intEvent('boundary - timer', svg(clock), true),
      intEvent('boundary - timer', svg(clock), false),

      intEvent('conditional', svg(note)),
      intEvent('boundary - conditional', svg(note), true),
      intEvent('boundary - conditional', svg(note), false),

      intEvent('catch - signal', svg(triangle)),
      intEvent('boundary - catch - signal', svg(triangle), true),
      intEvent('boundary - catch - signal', svg(triangle), false),
      intEvent('boundary - throw - signal', svg(triangleSolid), true),

      intEvent('catch - multiple', svg(pentagon)),
      intEvent('boundary - catch - multiple', svg(pentagon), true),
      intEvent('boundary - catch - multiple', svg(pentagon), false),
      intEvent('throw - multiple', svg(pentagonSolid)),

      intEvent('catch - parallel multiple', svg(cross)),
      intEvent('boundary - catch - parallel multiple', svg(cross), true),
      intEvent('boundary - catch - parallel multiple', svg(cross), false),

      intEvent('catch - escalation', svg(arrowUp)),
      intEvent('boundary - catch - escalation', svg(arrowUp), true),
      intEvent('boundary - catch - escalation', svg(arrowUp), false),
      intEvent('throw - escalation', svg(arrowUpSolid)),

      intEvent('boundary - catch - error', svg(bolt)),
      intEvent('boundary - catch - compensation', svg(rewind)),
      intEvent('throw - compensation', svg(rewindSolid)),
      intEvent('catch - link', svg(arrowRight)),
      intEvent('throw - link', svg(arrowRightSolid)),
      intEvent('boundary - catch - cancel', svg(cross)),

      { ...endEvent(undefined, undefined), featured: true },
      endEvent('message', svg(message)),
      endEvent('signal', svg(triangleSolid)),
      endEvent('multiple', svg(pentagonSolid)),
      endEvent('escalation', svg(arrowUpSolid)),
      endEvent('error', svg(boltSolid)),
      endEvent('compensation', svg(rewindSolid)),
      endEvent('cancel', svg(crossSolid)),
      endEvent('terminate', svg(circleSolid)),
    ],
  },
  businessProcessActivity: {
    title: 'Activities',
    shapes: [
      {
        variant: 'task',
        name: 'Task',
        featured: true,
      },
      task('Service', svg(gear)),
      task('Send', [svg(messageSolid), 0, -0.06]),
      task('Receive', [svg(message), 0, -0.06]),
      task('User', svg(user)),
      task('Manual', svg(hand)),
      task('Business rule', [svg(table), 0, -0.07]),
      task('Script', svg(script)),
      {
        variant: 'transaction',
        name: 'Transaction',
        properties: { borderStyle: 'double' },
        featured: true,
      },

      {
        variant: `event-sub-process`,
        name: `Event sub-process`,
        properties: { borderStyle: 'dashed' },
        featured: true,
      },
      subProcess('Message', svg(messageBordered), true),
      subProcess('Message', svg(messageBorderedDashed), false),
      subProcess('Timer', svg(clockBordered), true),
      subProcess('Timer', svg(clockBorderedDashed), false),
      subProcess('Conditional', svg(noteBordered), true),
      subProcess('Conditional', svg(noteBorderedDashed), false),
      subProcess('Signal', svg(triangleBordered), true),
      subProcess('Signal', svg(triangleBorderedDashed), false),
      subProcess('Multiple', svg(pentagonBordered), true),
      subProcess('Multiple', svg(pentagonBorderedDashed), false),
      subProcess('Parallel multiple', svg(plusSolidBordered), true),
      subProcess('Parallel multiple', svg(plusSolidBorderedDashed), false),
      subProcess('Escalation', svg(arrowUpBordered), true),
      subProcess('Escalation', svg(arrowUpBorderedDashed), false),
      subProcess('Error', svg(boltBordered), true),
      subProcess('Compensation', svg(rewindBordered), true),

      { ...callActivity('Abstract'), featured: true },
      callActivity('User', svg(user)),
      callActivity('Manual', svg(hand)),
      callActivity('Business rule', [svg(table), 0, -0.05]),
      callActivity('Script', svg(script)),
    ],
  },

  businessProcessGateway: {
    title: 'Gateways',
    shapes: [
      { ...gateway('Exclusive gateway - without marker'), featured: true },
      gateway('Exclusive gateway - with marker', svg(crossSolid)),
      gateway('Inclusive gateway', svg(circle)),
      gateway('Parallel gateway', svg(plusSolid)),
      gateway('Complex gateway', svg(star)),
      gateway('Event-based gateway', svg(pentagonBorderedDouble)),
      gateway('Event-based gateway to start a process', svg(pentagonBordered)),
      gateway(
        'Parallel event-based gateway to start a process',
        svg(plusBordered)
      ),
    ],
  },
  businessProcessData: {
    title: 'Data',
    shapes: [
      { ...dataObj('Data object'), featured: true },
      dataObj('Data object collection', undefined, svg(lines)),
      dataObj('Data input', svg(arrowRight)),
      dataObj('Data input collection', svg(arrowRight), svg(lines)),
      dataObj('Data output', svg(arrowRightSolid)),
      dataObj('Data output collection', svg(arrowRightSolid), svg(lines)),
    ],
  },
  businessProcessDataStore: {
    title: 'Data stores',
    shapes: [
      {
        variant: 'data-store',
        name: 'Data store',
      },
    ],
  },
};

export const featuredShapes = Object.entries(businessProcessShapes).flatMap(
  ([shapeName, group]) =>
    group.shapes
      .filter(shape => shape.featured)
      .map(shape => ({
        ...shape,
        shapeName: shapeName as BusinessProcessShapeNames,
      }))
);

// returns shapes grouped for the UI, and with the shapeName added to the definition
export const groupedShapes = Object.entries(businessProcessShapes)
  // tack the datastore group on to the end of the data shapes
  .filter(([shapeName]) => shapeName !== 'businessProcessDataStore')
  .map(([shapeName, group]) => ({
    title: group.title,
    shapes: group.shapes
      .map(s => ({ ...s, shapeName }))
      .concat(
        shapeName === 'businessProcessData'
          ? businessProcessShapes.businessProcessDataStore.shapes.map(s => ({
              ...s,
              shapeName: 'businessProcessDataStore',
            }))
          : []
      ) as (BusinessProcessShape & { shapeName: BusinessProcessShapeNames })[],
  }));

export const getBusinessProcessDefinition = (
  shapeName: BusinessProcessShapeNames,
  variant: string
) => {
  const definition = businessProcessShapes[shapeName].shapes.find(
    s => s.variant === variant
  );

  return definition;
};

// defines rendering options for a business process flow
export interface BusinessProcessFlow {
  id: string;
  name: string;
  options: StylusConnectorOptions;
}

const { NONE, CIRCLE, FILLED_ARROW, ARROW, POINTY_ARROW, DIAMOND, SLASH } =
  CONNECTOR_END_STYLE;
const { SOLID, DASHED, DOTTED } = STROKE_TYPES;

export const businessProcessFlows: BusinessProcessFlow[] = [
  {
    id: 'sequence',
    name: 'Sequence flow',
    options: {
      strokeType: SOLID,
      endStyles: [NONE, FILLED_ARROW],
    },
  },
  {
    id: 'message',
    name: 'Message flow',
    options: {
      strokeType: DASHED,
      endStyles: [CIRCLE, ARROW],
    },
  },
  {
    id: 'association',
    name: 'Association',
    options: { strokeType: DOTTED, endStyles: [NONE, NONE] },
  },
  {
    id: 'dataAssociation',
    name: 'Data association',
    options: {
      strokeType: DOTTED,
      endStyles: [NONE, POINTY_ARROW],
    },
  },
  {
    id: 'conditionalSequence',
    name: 'Conditional sequence flow',
    options: {
      strokeType: SOLID,
      endStyles: [DIAMOND, FILLED_ARROW],
    },
  },
  {
    id: 'defaultSequence',
    name: 'Default sequence flow',
    options: {
      strokeType: SOLID,
      endStyles: [SLASH, FILLED_ARROW],
    },
  },
  {
    id: 'initiatingMessage',
    name: 'Initiating message flow with decorator',
    options: {
      strokeType: DASHED,
      endStyles: [CIRCLE, ARROW],
      iconName: 'far/envelope',
    },
  },
  {
    id: 'nonInitiatingMessage',
    name: 'Non-initiating message flow with decorator',
    options: {
      strokeType: DASHED,
      endStyles: [CIRCLE, ARROW],
      iconName: 'fas/envelope',
    },
  },
];

export interface SwimlaneOptions {
  hasDivider: boolean;
}

export interface BusinessProcessSwimlane {
  id: string;
  name: string;
  icon: string;
  options: SwimlaneOptions;
}

export const businessProcessSwimlanes: BusinessProcessSwimlane[] = [
  {
    id: 'lane',
    name: 'Horizontal lane',
    icon: lane,
    options: { hasDivider: false },
  },
  {
    id: 'pool',
    name: 'Horizontal pool',
    icon: pool,
    options: { hasDivider: true },
  },
];

const generateFixture = () =>
  JSON.stringify(
    {
      '': 'Update using the generateFixture script in definitions.ts',
      version: '1.2.6',
      whiteboardId: '123',
      objects: [
        ...Object.entries(businessProcessShapes).flatMap(([shapeName, group]) =>
          group.shapes.map(shape => ({
            type: 'shape',
            shapeName,
            variant: shape.variant,
            stroke: '#333333',
            fill: '#FFFFFF',
            textContent: wrapText(shape.name, 20),
          }))
        ),

        // flow connectors
        ...businessProcessFlows.map((flow, i) => ({
          id: `${1000 + i}`,
          type: 'connector',
          stroke: '#333333',
          strokeWidth: 2,
          lineRouting: 20,
          attachments: [null, null],
          strokeType: 0,
          endStyles: [0, 0],
          ...flow.options,
        })),

        // and associated icon labels
        ...businessProcessFlows
          .filter(flow => flow.options.iconName)
          .map((flow, i) => ({
            type: 'shape',
            shapeName: 'icon',
            scaleX: 0.2,
            scaleY: 0.2,
            fill: '#333333',
            connectorId: `${1000 + i}`,
            labelPosition: 0.5,
            labelOrientation: 'inline',
            iconName: flow.options.iconName,
          })),
      ]
        .map((shape, index) => {
          const left = (index % 10) * 200;
          const top = Math.floor(index / 10) * 200;
          return {
            id: Math.random().toString().slice(2),
            ...shape,
            ...(shape.type === 'connector'
              ? {
                  points: [
                    { x: left, y: top },
                    { x: left + 150, y: top + 100 },
                  ],
                }
              : { left, top }),
          };
        })
        // convert array to keyed object
        .reduce((acc, shape) => ({ ...acc, [shape.id]: shape }), {}),
    },
    null,
    2
  );

// load demo whiteboard with ?generateFixture and copy from the console
if (
  process.env.NODE_ENV === 'development' &&
  window.location.search.includes('generateFixture')
) {
  console.log(generateFixture());
}
